import { User, UserManager } from "oidc-client-ts"
import React, { useContext, useEffect, useState } from "react"
import { createContext } from "react"

type SigninModes = "redirect" | "silent"

interface AuthContextData {
    user: User | null
    authAttempted: boolean
    userManager?: UserManager
    signinMode: SigninModes
}

const AuthContext = createContext<AuthContextData>({
    user: null,
    authAttempted: false,
    signinMode: "redirect",
})

interface AuthProviderProps {
    userManager?: UserManager
    signinMode: SigninModes
    children: React.ReactNode
    callbackUrl: string
}

export function AuthProvider({
    userManager,
    signinMode,
    children,
    callbackUrl
}: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null)
    const [authAttempted, setAuthAttempted] = useState(false)

    const tryLogin = async () => {
        if (!userManager) {
            throw new Error("OIDC service not initialized")
        }

        try {
            const cachedUser = await userManager.getUser()
            if (cachedUser && !cachedUser.expired && (!user || user.expired)) {
                console.log(`cachedUser: ${cachedUser.access_token}`)
                setUser(cachedUser)
                return
            }

            if (signinMode === 'silent') {
                await userManager.signinSilent()
            }
            else {
                await userManager.signinRedirect({
                    state: { returnUrl: location.pathname + location.search }
                })
            }
        }
        catch (error) {
            console.log(error)
        }
        finally {
            setAuthAttempted(true)
        }
    }

    useEffect(() => {
        if (!authAttempted && location.pathname === callbackUrl) {
            // Using SigninModes that redirect the browser lose this state
            setAuthAttempted(true)
        }

        if (!userManager) {
            return
        }

        if (!authAttempted && location.pathname !== callbackUrl) {
            tryLogin()
        }

        const onUserLoaded = (u: User) => setUser(u)
        userManager.events.addUserLoaded(onUserLoaded)
        return () => {
            userManager.events.removeUserLoaded(onUserLoaded)
        }
    }, [userManager, authAttempted])

    return (
        <AuthContext.Provider value={{ user, authAttempted, userManager, signinMode }} >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider")
    }

    return {
        user: context.user,
        authAttempted: context.authAttempted,
    }
}

export const useAuthInternal = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider")
    }

    return context
}